import { useEffect, useRef, useState } from 'react';
import { fetchCardPortfolio } from '../api/pokeApi';

const INITIAL_CARD_COUNT = 24;
const CARD_BATCH_SIZE = 12;

export default function PokeCards() {
  const [cardPortfolio, setCardPortfolio] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [visibleCardCount, setVisibleCardCount] = useState(INITIAL_CARD_COUNT);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    async function loadCardPortfolio() {
      try {
        const portfolioJson = await fetchCardPortfolio();
        setCardPortfolio(portfolioJson);
        setVisibleCardCount(INITIAL_CARD_COUNT);
      } catch (error) {
        setErrorMessage(error.message);
      }
    }

    loadCardPortfolio();
  }, []);

  useEffect(() => {
    if (!cardPortfolio || !loadMoreRef.current) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (!entry.isIntersecting) {
          return;
        }

        setVisibleCardCount((currentCount) =>
          Math.min(currentCount + CARD_BATCH_SIZE, cardPortfolio.data.length)
        );
      },
      {
        rootMargin: '600px 0px',
      }
    );

    observer.observe(loadMoreRef.current);

    return () => {
      observer.disconnect();
    };
  }, [cardPortfolio]);

  if (errorMessage) {
    return <p className="poke-cards__status">Unable to load cards: {errorMessage}</p>;
  }

  if (!cardPortfolio) {
    return <p className="poke-cards__status">Loading card portfolio...</p>;
  }

  const visibleCards = cardPortfolio.data.slice(0, visibleCardCount);
  const hasMoreCards = visibleCardCount < cardPortfolio.data.length;

  return (
    <>
      <section className="poke-cards" aria-label="Card portfolio">
        {visibleCards.map((card) => (
          <article
            className="poke-card"
            key={`${card.product_name}-${card.card_number}-${card.image_url}`}
          >
            <div className="poke-card__image-frame">
              <img
                className="poke-card__image"
                src={card.image_url}
                alt={card.product_name.trim()}
                loading="lazy"
              />
            </div>
            <div className="poke-card__details">
              <h2 className="poke-card__title">{card.product_name.trim()}</h2>
              <p className="poke-card__set">{card.catalog_group}</p>
              <p className="poke-card__meta">
                <span>{card.rarity}</span>
                <span className="poke-card__dot" aria-hidden="true">
                  •
                </span>
                <span>{card.card_number}</span>
              </p>
            </div>
          </article>
        ))}
      </section>
      {hasMoreCards ? <div className="poke-cards__sentinel" ref={loadMoreRef} /> : null}
    </>
  );
}
